import { dayjs, ElMessage } from 'element-plus'
import { getReportTime } from '@/api'

/**
 *
 * @param component 需要注册的组件
 * @param alias 组件别名
 * @returns any
 */
export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any
  comp.install = (app: any) => {
    app.component(comp.name || comp.displayName, component)
    if (alias) {
      app.config.globalProperties[alias] = component
    }
  }
  return component as T & Plugin
}

/**
 * @param str 需要转下划线的驼峰字符串
 * @returns 字符串下划线
 */
export const humpToUnderline = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

/**
 * @param str 需要转驼峰的下划线字符串
 * @returns 字符串驼峰
 */
export const underlineToHump = (str: string): string => {
  if (!str) return ''
  return str.replace(/\-(\w)/g, (_, letter: string) => {
    return letter.toUpperCase()
  })
}

/**
 * 驼峰转横杠
 */
export const humpToDash = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

export const setCssVar = (prop: string, val: any, dom = document.documentElement) => {
  dom.style.setProperty(prop, val)
}

export const getCssVar = (prop: string, dom = document.documentElement) => {
  return getComputedStyle(dom).getPropertyValue(prop)
}

/**
 * 查找数组对象的某个下标
 * @param {Array} ary 查找的数组
 * @param {Functon} fn 判断的方法
 */
// eslint-disable-next-line
export const findIndex = <T = Recordable>(ary: Array<T>, fn: Fn): number => {
  if (ary.findIndex) {
    return ary.findIndex(fn)
  }
  let index = -1
  ary.some((item: T, i: number, ary: Array<T>) => {
    const ret: T = fn(item, i, ary)
    if (ret) {
      index = i
      return ret
    }
  })
  return index
}

export const trim = (str: string) => {
  return str.replace(/(^\s*)|(\s*$)/g, '')
}

/**
 * @param {Date | number | string} time 需要转换的时间
 * @param {String} fmt 需要转换的格式 如 yyyy-MM-dd、yyyy-MM-dd HH:mm:ss
 */
export function formatTime(time: Date | number | string, fmt: string) {
  if (!time) return ''
  else {
    const date = new Date(time)
    const o = {
      'M+': date.getMonth() + 1,
      'd+': date.getDate(),
      'H+': date.getHours(),
      'm+': date.getMinutes(),
      's+': date.getSeconds(),
      'q+': Math.floor((date.getMonth() + 3) / 3),
      S: date.getMilliseconds()
    }
    if (/(y+)/.test(fmt)) {
      fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length))
    }
    for (const k in o) {
      if (new RegExp('(' + k + ')').test(fmt)) {
        fmt = fmt.replace(
          RegExp.$1,
          RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length)
        )
      }
    }
    return fmt
  }
}

/**
 * 生成随机字符串
 */
export function toAnyString() {
  const str: string = 'xxxxx-xxxxx-4xxxx-yxxxx-xxxxx'.replace(/[xy]/g, (c: string) => {
    const r: number = (Math.random() * 16) | 0
    const v: number = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString()
  })
  return str
}

/**
 * 首字母大写
 */
export function firstUpperCase(str: string) {
  return str.toLowerCase().replace(/( |^)[a-z]/g, (L) => L.toUpperCase())
}

/**
 * 把对象转为formData
 */
export function objToFormData(obj: Recordable) {
  const formData = new FormData()
  Object.keys(obj).forEach((key) => {
    formData.append(key, obj[key])
  })
  return formData
}

/**
 * 提取像素值：1px -> 1
 */
export function depx(value: string | number): number {
  if (typeof value === 'string') {
    if (value.endsWith('px')) {
      return Number(value.slice(0, value.length - 2))
    }
    return Number(value)
  }
  return value
}

interface IStatEvent {
  yeMianMingCheng: string
  pageId: string
  shiJianMingCheng: string
  eventId: string
  env: string
  yongHuId: string
  weiZhi: string
}

export type PartialStatEvent = Partial<IStatEvent>

export enum StatEventEnum {
  CLICK = import.meta.env.VITE_WEBFUNNY_CLICK,
  MENU_CLICK = import.meta.env.VITE_WEBFUNNY_MENU_CLICK
}

export type StatEventType = keyof StatEventEnum

export function statSumbit(type: StatEventType, params: PartialStatEvent) {
  window?.webfunnyEvent(type).trackEvent(params)
}

function reduceFontSize(datetime: string) {
  const datetimeArr = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉']
  return datetime
    .split('')
    .map((char: any) => datetimeArr[Number(char)] || char)
    .join('')
}
/**
 * 水印配置整合
 */
export function transformWaterMarkConfig(data: any) {
  const isClose = !data.visibleWmContent
  const visibleWmContentAry = data.visibleWmContent.split(' ')
  const datetime = visibleWmContentAry.at(-1) || ''
  const smallDatetime = reduceFontSize(datetime)
  const otherContent = visibleWmContentAry.slice(0, -1).join(' ')

  const config = {
    content: `${otherContent} ${smallDatetime}`,
    invisibleContent: data.invisibleWmContent,
    invisibleFontSize: data.invisibleWmFontSize,
    font: {
      fontWeight: 300,
      fontSize: data.visibleWmFontSize,
      // color: 'rgba(192,193,204,0.1)'
      color: `rgba(${data.visibleWmColor.red || '0'}, ${data.visibleWmColor.green || '0'}, ${data.visibleWmColor.blue || '0'}, ${data.visibleWmColor.alpha / 100})`
    },
    zIndex: 100,
    rotate: -22,
    visibleWmDensityLevel: data.visibleWmDensityLevel,
    invisibleWmDensityLevel: data.invisibleWmDensityLevel,
    gap: [100, 50] as [number, number],
    offset: [] as unknown as [number, number],
    isClose: isClose,
    originData: { ...data, otherContent, smallDatetime: datetime }
  }
  return config
}

// 获取文件名
export function getFileName(fileName: string) {
  return fileName.substring(0, fileName.lastIndexOf('.'))
}

/**
 * 获取文件名后缀
 */
export function getFileExtension(fileName: string) {
  return fileName.substring(fileName.lastIndexOf('.'))
}

/**
 * 将数字转换为百分比并保留一位小数，例如：0.1 -> +10.0%
 * @param value
 * @returns
 */
// export function toPercentage(value: number): string {
//   return value ? `${value > 0 ? '+' : ''}${(value * 100).toFixed(1)}%` : '--'
// }
export function toPercentage(value: number): string {
  return value !== undefined && value !== null
    ? `${value > 0 ? '+' : ''}${value.toFixed(1)}%`
    : '--'
}

/**
 * 将数字转换为百分比并保留一位小数，例如：0.1 -> 10.0%
 * @param value
 * @returns
 */
// export function toPercentageUnit(value: number): string {
//   return value ? `${(value * 100).toFixed(1)}%` : '--'
// }

export function toPercentageUnit(value: number, fixed = 1): string {
  return value !== undefined && value !== null ? `${(+value).toFixed(fixed)}%` : '--'
}

/**
 * 将金额保留两位小数，例如：1000 -> 1,000.00
 */
export function toCurrency(value: number, fixed = 2): string {
  if (value === null || value === undefined) return '--'

  const numberValue = Number(value)
  if (Number.isNaN(numberValue)) return '--'

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fixed,
    maximumFractionDigits: fixed
  }).format(numberValue)
}

/**
 * 增加正好 +
 */
export function toCurrencyUnit(value: string | number | null | undefined, fixed = 2): string {
  if (value === null || value === undefined || value === '') return '--'

  const numberValue = Number(value)
  if (Number.isNaN(numberValue)) return '--'

  const res = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fixed,
    maximumFractionDigits: fixed
  }).format(numberValue)

  return numberValue > 0 ? `+${res}` : res
}

export function getMetricFixed(isMoney: boolean, quantityFixed = 4, moneyFixed = 2): number {
  return isMoney ? moneyFixed : quantityFixed
}

export function formatMetricValue(
  value: number,
  isMoney: boolean,
  quantityFixed = 4,
  moneyFixed = 2
): string {
  return toCurrency(value, getMetricFixed(isMoney, quantityFixed, moneyFixed))
}

export function formatMetricChangeValue(
  value: number,
  isMoney: boolean,
  quantityFixed = 4,
  moneyFixed = 2
): string {
  return toCurrencyUnit(value, getMetricFixed(isMoney, quantityFixed, moneyFixed))
}

export const toFormatPercent = (value) => {
  return value ? `${value}%` : '--'
}

/**
 * 从接口获取报表时间的前一天
 */
export const renderUpdateDate = async (code: string) => {
  const { data } = await getReportTime(code)
  return data?.newTime
}

/**
 * 初始化时间筛选器的时间范围
 */

export const formatDefaultData = (dateNum: number) => {
  // 当月13日及之前默认月份上上月，14号及之后默认为上月
  let startTime
  let endTime
  if (dayjs().date() <= dateNum) {
    startTime = dayjs().subtract(2, 'month').date(1).startOf('day')
    endTime = dayjs().subtract(2, 'month').endOf('month').endOf('day')
  } else {
    startTime = dayjs().subtract(1, 'month').date(1).startOf('day')
    endTime = dayjs().subtract(1, 'month').endOf('month').endOf('day')
  }
  return [startTime, endTime]
}

// 获取上个月的时间
export const getLastMonth = () => {
  const startTime = dayjs().subtract(1, 'month').date(1).startOf('day')
  const endTime = dayjs().subtract(1, 'month').endOf('month').endOf('day')
  return [startTime, endTime]
}

/**
 * 获取上个星期日对应的月份开始时间和结束时间
 */
export const getLastWeekMonth = () => {
  // 获取当前日期
  const today = dayjs()

  // 获取上个星期日的日期
  // 在dayjs中，0表示星期日，1表示星期一，依此类推
  // 如果今天是星期日(0)，则获取七天前的日期
  // 否则，获取上个星期日的日期
  const lastSunday =
    today.day() === 0 ? today.subtract(7, 'day') : today.subtract(today.day(), 'day')

  // 获取上个星期日所在月份的第一天
  const firstDayOfMonth = lastSunday.startOf('month')

  // 获取上个星期日所在月份的最后一天
  const lastDayOfMonth = lastSunday.endOf('month')

  return [firstDayOfMonth, lastDayOfMonth]
}

/**
 * 解析范围字符串，支持多种格式：
 * - '<=3000万' → { minCount: '', maxCount: '3000' }
 * - '>3000万,<=5000万' → { minCount: '3000', maxCount: '5000' }
 * - '<=100盒' → { minCount: '', maxCount: '100' }
 * - '>10,<=50' → { minCount: '10', maxCount: '50' }
 * - '>=50,<100' → { minCount: '50', maxCount: '100' }
 * @param rangeStr 范围字符串
 * @returns 解析后的范围对象 { minCount, maxCount }
 */
export const parseRangeString = (rangeStr: string) => {
  const result = {
    minCount: '',
    maxCount: ''
  }

  // 移除所有空格，按逗号分割成多个部分
  const cleanStr = rangeStr.replace(/\s+/g, '')
  const parts = cleanStr.split(',')

  // 遍历每个部分，根据符号判断是下限还是上限
  parts.forEach((part) => {
    // 匹配比较符号和数字：支持 <、<=、>、>=
    const match = part.match(/^([<>]=?)(\d+)/)
    if (!match) return

    const operator = match[1] // 比较符号：<、<=、>、>=
    const number = match[2] // 数字部分

    // 根据符号判断是 minCount 还是 maxCount
    if (operator.startsWith('>')) {
      // > 或 >= 表示下限
      result.minCount = number
    } else if (operator.startsWith('<')) {
      // < 或 <= 表示上限
      result.maxCount = number
    }
  })

  return result
}

/**
 * 统一处理范围的输入（金额、盒数等）
 * @param data 查询数据对象
 * @param fieldName 范围字段名（如 'imsAmount'）
 * @param showError 是否显示错误提示（默认 true）
 * @returns 范围对象 { minCount, maxCount } 或 false（验证失败）
 */
export const dealRangeValue = (
  data: any,
  fieldName: string = 'imsAmount',
  showError: boolean = true
) => {
  const rangeVo = {
    minCount: '',
    maxCount: ''
  }

  const rangeValue = data[fieldName]

  // 如果没有选择范围，直接返回
  if (!rangeValue) {
    return rangeVo
  }

  // 自定义范围
  if (rangeValue === 'self') {
    if (!data.minMaxCount?.min) {
      if (showError) {
        ElMessage.error('请输入起始值')
      }
      return false
    }
    if (!data.minMaxCount?.max) {
      if (showError) {
        ElMessage.error('请输入结束值')
      }
      return false
    }
    if (Number(data.minMaxCount.min) > Number(data.minMaxCount.max)) {
      if (showError) {
        ElMessage.error('结束值不能小于起始值，请检查后重新输入')
      }
      return false
    }
    rangeVo.minCount = data.minMaxCount.min
    rangeVo.maxCount = data.minMaxCount.max
  } else {
    // 预设范围，使用通用解析函数
    const parsed = parseRangeString(rangeValue)
    rangeVo.minCount = parsed.minCount
    rangeVo.maxCount = parsed.maxCount
  }

  return rangeVo
}

/**
 * 格式化默认值
 */
export const defaultFormatter = (row: any, column: any, cell: any, index: number) => {
  //cell是null或者undefined则返回--
  if (cell === null || cell === undefined || cell === '') {
    return '--'
  }
  return cell
}

//格式化除了第一行以外的默认值
export const defaultFormatterExceptTotal = (row: any, column: any, cell: any, index: number) => {
  return index < 2 ? cell : cell === null || cell === undefined || cell === '' ? '--' : cell
}
